import type { Credentials } from '@aws-sdk/client-cognito-identity';
import { S3Client } from '@aws-sdk/client-s3';
import { Upload as UploadClient } from '@aws-sdk/lib-storage';
import type { MimeTypeExtended } from 'interfaces/app';
import type * as DB from 'interfaces/database';
import type { UploadFileAdapterLifecycle } from './adapters';
import { NonRetryableUploadError } from './machines';

type AwsCredentials = (
	Required<Pick<Credentials, 'AccessKeyId' | 'SecretKey'>>
	& Pick<Credentials, 'SessionToken'>
);

type ConfigLike = {
	'upload.accelerate': boolean;
};

export type CognitoCredentialsProvider = {
	checkCredentials: () => Promise<boolean>;
	getCredentials: () => Credentials | undefined;
};

export type AwsUploaderFile = {
	id: string;
	file: File;
	fileName: string;
	fileType: MimeTypeExtended;
};

export type S3ClientLike = {
	destroy?: () => void;
};

export type UploadClientLike = {
	on: (event: 'httpUploadProgress', handler: (progress: { loaded?: number;
		total?: number }) => void) => void;
	done: () => Promise<{ Location?: string }>;
	abort: () => Promise<void>;
};

export type UploadClientFactory = (input: {
	s3: S3ClientLike;
	bucket: string;
	objectKey: string;
	body: File;
	contentType: MimeTypeExtended;
}) => UploadClientLike;

export type S3ClientFactory = (input: {
	region: string;
	useAccelerateEndpoint: boolean;
	requestTimeoutMs: number;
	credentials?: {
		accessKeyId: string;
		secretAccessKey: string;
		sessionToken?: string;
	};
}) => S3ClientLike;

export type AwsUploaderOptions = {
	cognito: CognitoCredentialsProvider;
	getBucketModel: () => DB.BucketModel | undefined;
	getConfig: () => ConfigLike;
	requestTimeoutMs?: number;
	/** Override for tests. Defaults to constructing an AWS SDK S3Client. */
	s3ClientFactory?: S3ClientFactory;
	/** Override for tests. Defaults to constructing an AWS SDK lib-storage Upload. */
	uploadClientFactory?: UploadClientFactory;
};

const fiveMinutes = 5 * 60 * 1000;
const defaultRequestTimeoutMs = fiveMinutes;

const defaultS3ClientFactory: S3ClientFactory = (input) => new S3Client({
	region: input.region,
	useAccelerateEndpoint: input.useAccelerateEndpoint,
	requestHandler: {
		requestTimeout: input.requestTimeoutMs,
	},
	credentials: input.credentials,
}) as S3ClientLike;

const defaultUploadClientFactory: UploadClientFactory = (input) => new UploadClient({
	params: {
		Bucket: input.bucket,
		ACL: 'private',
		Key: input.objectKey,
		Body: input.body,
		ContentType: input.contentType,
	},
	queueSize: 1,
	client: input.s3 as S3Client,
}) as unknown as UploadClientLike;

/**
 * Wraps the AWS S3 upload to the auto-expire bucket. Credentials and the S3
 * client are cached on the instance and rotated automatically when Cognito
 * returns new credentials. Use one instance per Upload controller — the cache
 * lives for the lifetime of that controller.
 *
 * The S3 and UploadClient constructors are injected via the options to keep
 * the class fully unit-testable without mocking @aws-sdk module entries.
 */
export class AwsAutoExpireUploader {
	private credentials: AwsCredentials | undefined;

	private s3: S3ClientLike | undefined;

	/**
	 * Identity of the credentials baked into the cached `s3` client. The client
	 * signs every request with these values, so it must be torn down and rebuilt
	 * whenever the stored credentials change — see refreshCredentials/getS3Client.
	 */
	private s3CredentialsIdentity: string | undefined;

	private readonly s3ClientFactory: S3ClientFactory;

	private readonly requestTimeoutMs: number;

	private readonly uploadClientFactory: UploadClientFactory;

	public constructor(private readonly options: AwsUploaderOptions) {
		this.requestTimeoutMs = options.requestTimeoutMs ?? defaultRequestTimeoutMs;
		this.s3ClientFactory = options.s3ClientFactory ?? defaultS3ClientFactory;
		this.uploadClientFactory = options.uploadClientFactory ?? defaultUploadClientFactory;
	}

	public async runUpload(
		file: AwsUploaderFile,
		lifecycle: UploadFileAdapterLifecycle,
	): Promise<string> {
		const lastDot = file.fileName.lastIndexOf('.');
		const fileExtension = lastDot >= 0 ? file.fileName.slice(lastDot + 1) : '';
		const objectKey = fileExtension ? `${file.id}.${fileExtension}` : `${file.id}`;

		await this.refreshCredentials();

		/*
		 * refreshCredentials is the only await between entering runUpload and
		 * registering the abort listener below; everything in between runs
		 * synchronously, so an abort that fires during the refresh window is
		 * always observed here. There is no microtask gap in which an early
		 * abort could slip past unnoticed.
		 */
		if (lifecycle.abortSignal.aborted) {
			throw new Error('Upload aborted');
		}

		const bucketModel = this.options.getBucketModel();

		if (!bucketModel?.label) {
			throw new NonRetryableUploadError('Missing required auto-expire bucket model');
		}

		const s3 = this.getS3Client(bucketModel);
		const uploadClient = this.uploadClientFactory({
			s3,
			bucket: bucketModel.label,
			objectKey,
			body: file.file,
			contentType: file.fileType,
		});
		let resetUploadStallWatchdog = (): void => {};

		uploadClient.on(
			'httpUploadProgress',
			(progress) => {
				resetUploadStallWatchdog();

				if (
					typeof progress.loaded !== 'undefined'
					&& typeof progress.total !== 'undefined'
				) {
					lifecycle.reportProgress(
						progress.loaded,
						progress.total,
					);
				}
			},
		);

		/*
		 * Both the abort signal and the request timeout can race to cancel the
		 * upload. Funnel every cancellation through a single-shot abort so the
		 * underlying multipart upload is aborted at most once, and surface any
		 * rejection from abort() instead of dropping it on the floor.
		 */
		let aborted = false;
		const abortOnce = (): void => {
			if (aborted) {
				return;
			}

			aborted = true;
			uploadClient.abort().catch((error: unknown) => {
				console.error(
					'Failed to abort auto-expire S3 upload',
					error,
				);
			});
		};

		/*
		 * Wire the abort signal BEFORE awaiting done() so that mid-flight
		 * cancellation triggered by the item actor's cleanup callback actually
		 * aborts the in-progress S3 multipart upload. The previous design
		 * returned `abort` only after done() resolved, silently dropping the
		 * abort window where it mattered most.
		 */
		lifecycle.abortSignal.addEventListener(
			'abort',
			abortOnce,
			{
				once: true,
			},
		);

		try {
			const result = await this.waitForUploadCompletion(
				uploadClient,
				abortOnce,
				(resetStallWatchdog) => {
					resetUploadStallWatchdog = resetStallWatchdog;
				},
			);

			if (!result.Location) {
				throw new NonRetryableUploadError('Missing url of uploaded file');
			}

			return result.Location;
		} finally {
			lifecycle.abortSignal.removeEventListener(
				'abort',
				abortOnce,
			);
		}
	}

	/**
	 * Race upload completion against a stall watchdog. The watchdog is reset by
	 * progress ticks, so slow-but-moving uploads can run longer than one request
	 * timeout while genuinely stalled uploads still abort.
	 * @param uploadClient - AWS upload client wrapping the multipart transfer.
	 * @param abortOnce - Single-shot cancellation funnel for the upload client.
	 * @param registerProgressWatchdogReset - Receives the reset callback used by the progress listener.
	 * @returns Promise resolving to the AWS upload result.
	 */
	private waitForUploadCompletion(
		uploadClient: UploadClientLike,
		abortOnce: () => void,
		registerProgressWatchdogReset: (resetStallWatchdog: () => void) => void,
	): Promise<{ Location?: string }> {
		let timeoutId: ReturnType<typeof setTimeout> | undefined;
		const clearStallTimer = (): void => {
			if (typeof timeoutId !== 'undefined') {
				clearTimeout(timeoutId);
				timeoutId = undefined;
			}
		};
		const timeoutPromise = new Promise<never>((_, reject) => {
			const resetStallTimer = (): void => {
				clearStallTimer();
				timeoutId = setTimeout(
					() => {
						reject(new Error(`Upload timed out after ${this.requestTimeoutMs}ms`));
						abortOnce();
					},
					this.requestTimeoutMs,
				);
			};

			registerProgressWatchdogReset(resetStallTimer);
			resetStallTimer();
		});

		/*
		 * Promise.race attaches a reaction to BOTH the done() and timeout
		 * promises, so a late rejection from the losing done() promise stays
		 * handled and never escalates to an unhandled rejection — no extra
		 * .catch on the loser is required.
		 */
		return Promise
			.race([
				uploadClient.done(),
				timeoutPromise,
			])
			.finally(() => {
				clearStallTimer();
				registerProgressWatchdogReset(() => {});
			});
	}

	private async refreshCredentials(): Promise<void> {
		/*
		 * checkCredentials() reports whether Cognito *thinks* the credentials
		 * changed, but it cannot be trusted to gate the S3 client rebuild on its
		 * own: credentials can rotate underneath us while it still returns false.
		 * We always re-read the credentials and let getS3Client decide whether the
		 * cached client is stale by comparing the actual credential identity.
		 */
		await this.options.cognito.checkCredentials();
		const credentials = this.options.cognito.getCredentials();

		if (!credentials?.AccessKeyId || !credentials?.SecretKey) {
			throw new NonRetryableUploadError('Missing required credentials');
		}

		this.credentials = credentials as AwsCredentials;
	}

	private getS3Client(bucketModel: DB.BucketModel): S3ClientLike {
		const credentialsIdentity = this.credentialsIdentity();

		/*
		 * Reuse the cached client only while it still signs with the exact
		 * credentials we currently hold; otherwise tear it down so we never sign
		 * requests with stale credentials.
		 */
		if (this.s3 && this.s3CredentialsIdentity === credentialsIdentity) {
			return this.s3;
		}

		this.s3?.destroy?.();

		const config = this.options.getConfig();

		this.s3 = this.s3ClientFactory({
			region: bucketModel.region,
			useAccelerateEndpoint: config['upload.accelerate'],
			requestTimeoutMs: this.requestTimeoutMs,
			credentials: this.credentials
				? {
					accessKeyId: this.credentials.AccessKeyId,
					secretAccessKey: this.credentials.SecretKey,
					sessionToken: this.credentials.SessionToken,
				}
				: undefined,
		});
		this.s3CredentialsIdentity = credentialsIdentity;

		return this.s3;
	}

	private credentialsIdentity(): string | undefined {
		if (!this.credentials) {
			return undefined;
		}

		return JSON.stringify([
			this.credentials.AccessKeyId,
			this.credentials.SecretKey,
			this.credentials.SessionToken,
		]);
	}
}
